/* eslint-disable react-hooks/exhaustive-deps -- setBanner is a stable state wrapper */
import i18n from "../../i18n";
import { Trans } from "react-i18next";
import React, { useState, useRef, useCallback, useEffect } from 'react';
import './Addemployeepage.css';
// import { resolveApiUrl, resolveOrgApiUrl } from "../../shared/resolveApiUrl";
import { resolveApiUrl, resolveOrgApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import {
  getOnboardingFeatures,
  resolveOnboardingActionConfig,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  isLookupRateLimitError,
  parseOnboardingErrorResponse,
  handleOnboardingApiError
} from
"./onboardingFeatureAccess";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"../../components/ui/select";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger } from
"../../components/ui/popover";

/* ─── Employee photo: search DTO exposes empPic / empPicBase64 / hasEmpPic ─── */
const IMAGE_BASE64_PREFIXES = ["/9j/", "iVBORw0", "R0lGOD", "UklGR", "Qk0"]; // jpeg/png/gif/webp/bmp

function bytesToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.slice(i, i + chunk));
  }
  return btoa(binary);
}

function looksLikeImageBase64(str) {
  if (typeof str !== "string") return false;
  const cleaned = str.includes(",") ? str.split(",").pop() : str.trim();
  if (!cleaned || cleaned.length < 64) return false;
  if (cleaned === "null" || cleaned === "undefined") return false;
  return IMAGE_BASE64_PREFIXES.some((p) => cleaned.startsWith(p)) || cleaned.length > 200;
}

function extractPhotoValue(value, depth = 0) {
  if (value == null || depth > 4) return null;
  if (typeof value === "boolean" || typeof value === "number") return null;

  if (typeof value === "string") {
    const str = value.trim();
    if (!str || str === "null" || str === "undefined") return null;
    if (str.startsWith("http://") || str.startsWith("https://") || str.startsWith("blob:") || str.startsWith("data:")) {
      return str;
    }
    return looksLikeImageBase64(str) ? str : null;
  }

  if (Array.isArray(value) && value.length > 64 && typeof value[0] === "number") {
    return bytesToBase64(Uint8Array.from(value));
  }

  if (typeof value === "object") {
    return (
      extractPhotoValue(value.empPicBase64, depth + 1) ||
      extractPhotoValue(value.empPic, depth + 1) ||
      extractPhotoValue(value.photoBase64, depth + 1) ||
      extractPhotoValue(value.base64, depth + 1) ||
      extractPhotoValue(value.data, depth + 1) ||
      extractPhotoValue(value.content, depth + 1) ||
      extractPhotoValue(value.bytes, depth + 1));

  }
  return null;
}

function findPhotoInRecord(data) {
  if (!data || typeof data !== "object") return null;

  const named = [
  data.empPicBase64,
  data.empPic,
  data.photoBase64,
  data.photo,
  data.emp_pic_base64,
  data.emp_pic,
  data.photoUrl,
  data.empPicUrl,
  data.profilePicBase64,
  data.profilePic,
  data.employeePhoto,
  data.employeePhotoBase64,
  data.avatar];

  for (const candidate of named) {
    const found = extractPhotoValue(candidate);
    if (found) return found;
  }

  // Fallback: scan string fields for embedded image base64 (DTO field names vary)
  for (const [key, value] of Object.entries(data)) {
    if (/pic|photo|avatar|image/i.test(key)) {
      const found = extractPhotoValue(value);
      if (found) return found;
    }
  }
  for (const value of Object.values(data)) {
    if (typeof value === "string" && looksLikeImageBase64(value)) {
      return value.trim();
    }
  }
  return null;
}

function recordHasPhotoFlag(data) {
  if (!data || typeof data !== "object") return false;
  return [data.hasEmpPic, data.hasPhoto, data.photoAvailable, data.hasProfilePic, data.empPicUploaded]
  .some((flag) => flag === true || flag === "true" || flag === 1 || flag === "Y" || flag === "y");
}

function applyPhotoToState(raw, setPhotoSrc, setPhotoB64) {
  const str = String(raw).trim();
  if (str.startsWith("http://") || str.startsWith("https://") || str.startsWith("blob:")) {
    setPhotoSrc(str);
    setPhotoB64(null);
    return;
  }
  const cleanB64 = str.includes(",") ? str.split(",").pop() : str;
  setPhotoB64(cleanB64);
  setPhotoSrc(str.startsWith("data:") ? str : `data:image/jpeg;base64,${cleanB64}`);
}

/**
 * Resolve the tenant-configured photo download API when search returns hasEmpPic
 * without inline bytes. Falls back to the HRMS attachment download convention.
 */
function resolveEmployeePhotoDownload(empId) {
  const features = getOnboardingFeatures(["Employee Information", "Employee Info", "Add Employee"]);
  const match = features.find((feature) => {
    const name = String(feature?.feature || "").toLowerCase();
    const api = String(feature?.api || "").toLowerCase();
    return (
      (name.includes("download") && (name.includes("photo") || name.includes("pic") || name.includes("image"))) ||
      api.includes("/download") ||
      api.includes("/photo") ||
      api.includes("/pic"));

  });

  const downloadConfig = resolveOnboardingActionConfig({
    submoduleNames: ["Employee Information", "Employee Info", "Add Employee"],
    action: "download",
    fallback: {
      title: "Download Employee Photo",
      serviceCode: match?.code || "HR01DD",
      endpoint: match?.api || `/saas/hrms/emp-info/download/${empId}`,
      method: "GET"
    }
  });

  const endpoint = String(downloadConfig.endpoint || `/saas/hrms/emp-info/download/${empId}`)
  .replace(/\{empId\}/gi, String(empId))
  .replace(/\{id\}/gi, String(empId))
  .replace(/\{documentType\}/gi, "photo")
  .replace(/\{docType\}/gi, "photo");

  return {
    url: endpoint,
    serviceCode: downloadConfig.serviceCode || match?.code || "HR01DD"
  };
}

async function fetchEmployeePhotoFromBackend(empId, headers) {
  const { url, serviceCode } = resolveEmployeePhotoDownload(empId);
  if (!url) return null;

  const response = await fetch(resolveApiUrl(url), {
    method: "GET",
    headers: {
      ...headers,
      "x-service-code": serviceCode,
      Accept: "*/*"
    }
  });
  if (!response.ok) return null;

  const contentType = String(response.headers.get("content-type") || "").toLowerCase();
  if (contentType.includes("application/json")) {
    const body = await response.json().catch(() => null);
    const nested = findPhotoInRecord(body?.data) || findPhotoInRecord(body) || extractPhotoValue(body?.data) || extractPhotoValue(body);
    return nested ? { kind: "inline", value: nested } : null;
  }

  const blob = await response.blob();
  if (!blob || blob.size < 32) return null;
  return { kind: "blob", value: blob };
}

/* ─── Icon helpers ─────────────────────────────────────── */
const IconBack = () =>
<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>;


/* ─── Constants ────────────────────────────────────────── */
const SERVICE_CODE = 'HR01A';
const API_ENDPOINT = '/saas/hrms/emp-info/add';
const STATES_ENDPOINT = '/saas/hrms/org/states';
const DISTRICTS_BY_STATE_ENDPOINT = '/saas/hrms/org/cities/by-state';
const MAX_PHOTO_BYTES = 6990506; // ~5 MB after base64 overhead

/* ─── Validation rules ─────────────────────────────────── */
function getFieldLabels() {
  return {
    firstName: i18n.t('onboarding.labels.firstName'),
    middleName: i18n.t('onboarding.labels.middleName'),
    lastName: i18n.t('onboarding.labels.lastName'),
    dateOfBirth: i18n.t('onboarding.labels.dateOfBirth'),
    email: i18n.t('common.email'),
    phone: i18n.t('onboarding.labels.phoneNumber'),
    contactType: i18n.t('onboarding.labels.contactType'),
    addressHouseNo: i18n.t('onboarding.labels.houseNo'),
    addressStreetName: i18n.t('onboarding.labels.streetName'),
    addressLocationArea: i18n.t('onboarding.labels.areaLocation'),
    addressDistrict: i18n.t('onboarding.labels.district'),
    addressState: i18n.t('onboarding.labels.state'),
    addressPincode: i18n.t('onboarding.labels.pincode'),
    addressDigipin: i18n.t('onboarding.labels.digipin'),
    addressGmapLink: i18n.t('onboarding.labels.googleMapsLink')
  };
}

function getRules() {
  return {
    firstName: (v) => !v.trim() ? i18n.t('onboarding.errors.firstNameRequired') : v.length > 100 ? i18n.t('onboarding.errors.max100Characters') : '',
    lastName: (v) => !v.trim() ? i18n.t('onboarding.errors.lastNameRequired') : v.length > 100 ? i18n.t('onboarding.errors.max100Characters') : '',
    middleName: (v) => v && v.length > 100 ? i18n.t('onboarding.errors.max100Characters') : '',
    dateOfBirth: (v) => !v ? i18n.t('onboarding.errors.dateOfBirthRequired') : new Date(v) >= new Date() ? i18n.t('onboarding.errors.mustBeInPast') : '',
    email: (v) => !v ? i18n.t('onboarding.errors.emailRequired') : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? i18n.t('onboarding.errors.invalidEmail') : v.length > 150 ? i18n.t('onboarding.errors.max150Characters') : '',
    phone: (v) => !v.trim() ? i18n.t('onboarding.errors.phoneRequired') : !/^[0-9]{10,20}$/.test(v) ? i18n.t('onboarding.errors.digitsOnly10to20') : '',
    contactType: (v) => !v ? i18n.t('onboarding.errors.contactTypeRequired') : !['Phone', 'Email', 'Both'].includes(v) ? i18n.t('onboarding.errors.invalidContactType') : '',
    addressHouseNo: (v) => !v.trim() ? i18n.t('onboarding.errors.houseNumberRequired') : v.length > 50 ? i18n.t('onboarding.errors.max50Characters') : '',
    addressStreetName: (v) => !v.trim() ? i18n.t('onboarding.errors.streetNameRequired') : v.length > 50 ? i18n.t('onboarding.errors.max50Characters') : '',
    addressLocationArea: (v) => !v.trim() ? i18n.t('onboarding.errors.locationAreaRequired') : v.length > 50 ? i18n.t('onboarding.errors.max50Characters') : '',
    addressDistrict: (v) => !v.trim() ? i18n.t('onboarding.errors.districtRequired') : v.length > 50 ? i18n.t('onboarding.errors.max50Characters') : '',
    addressState: (v) => !v.trim() ? i18n.t('onboarding.errors.stateRequired') : v.length > 50 ? i18n.t('onboarding.errors.max50Characters') : '',
    addressPincode: (v) => !v.trim() ? i18n.t('onboarding.errors.pincodeRequired') : !/^[0-9]{6}$/.test(v) ? i18n.t('onboarding.errors.mustBe6Digits') : '',
    addressDigipin: (v) => v && v.length > 10 ? i18n.t('onboarding.errors.max10Characters') : '',
    addressGmapLink: (v) => !v.trim() ? i18n.t('onboarding.errors.googleMapsLinkRequired') : ''
  };
}

const REQUIRED_FIELDS = ['firstName', 'lastName', 'dateOfBirth', 'phone', 'email', 'contactType', 'addressHouseNo', 'addressStreetName', 'addressLocationArea', 'addressDistrict', 'addressState', 'addressPincode', 'addressGmapLink'];

const INITIAL = {
  firstName: '', middleName: '', lastName: '', dateOfBirth: '',
  email: '', phone: '', contactType: '',
  addressHouseNo: '', addressStreetName: '', addressLocationArea: '',
  addressDistrict: '', addressState: '', addressPincode: '',
  addressDigipin: '', addressGmapLink: ''
};

function normalizeLookupOptions(payload, idKeys = [], labelKeys = []) {
  const candidates = [
  payload,
  payload?.data,
  payload?.content,
  payload?.result,
  payload?.data?.content,
  payload?.data?.result,
  payload?.data?.result?.content,
  payload?.records,
  payload?.items,
  payload?.rows,
  payload?.states,
  payload?.cities,
  payload?.districts,
  payload?.data?.states,
  payload?.data?.cities,
  payload?.data?.districts];


  const list = candidates.find((value) => Array.isArray(value));
  if (!Array.isArray(list)) return [];

  return list
  .map((item) => {
    if (!item || typeof item !== 'object') return null;
    const id = [
    ...idKeys.map((key) => item?.[key]),
    item?.stateId,
    item?.state_id,
    item?.cityId,
    item?.city_id,
    item?.districtId,
    item?.district_id,
    item?.id]
    .find((value) => value !== null && value !== undefined && String(value).trim());
    const label = [
    ...labelKeys.map((key) => item?.[key]),
    item?.stateName,
    item?.state_name,
    item?.cityName,
    item?.city_name,
    item?.districtName,
    item?.district_name,
    item?.name,
    item?.label]
    .find((value) => value !== null && value !== undefined && String(value).trim());

    if (!id || !label) return null;
    return { value: String(id).trim(), label: String(label).trim() };
  })
  .filter(Boolean);
}

/* ─── Field component ──────────────────────────────────── */
function Field({ id, label, required, error, children }) {
  return (
    <div className={`aep-field${id === 'addressGmapLink' || id === 'contactType' ? ' span2' : ''}`}>
      <label className={`aep-label${required ? ' required' : ''}`} htmlFor={id}>{label}</label>
      {children}
      <p className="aep-error" role="alert">{error || ''}</p>
    </div>);

}

/* ─── AddEmployeePage ──────────────────────────────────── */
export default function AddEmployeePage({
  accessToken,
  tenantCode,
  initialEmployeeId = "",
  onBack,
  onSuccess,
  breadcrumb,
  isTabMode = false,
  isActive = true,
  onRateLimitError,
  saveTriggerRef
}) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSubmit
      };
    }
  });
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [photoB64, setPhotoB64] = useState(null);
  const [photoSrc, setPhotoSrc] = useState(null);
  const photoObjectUrlRef = useRef(null);

  const clearPhotoObjectUrl = useCallback(() => {
    if (photoObjectUrlRef.current) {
      URL.revokeObjectURL(photoObjectUrlRef.current);
      photoObjectUrlRef.current = null;
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null); // { type: 'success'|'error', msg: string }

  // Derived — available both in event handlers and JSX
  const isEdit = !!initialEmployeeId;
  const reportLookupRateLimit = useCallback((message, status, errorBody) => {
    if (!isLookupRateLimitError(message, status, errorBody)) return false;
    if (typeof onRateLimitError === "function") {
      onRateLimitError();
    }
    return true;
  }, [onRateLimitError]);

  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (reportLookupRateLimit(next.msg)) {
          return prev;
        }
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const fileRef = useRef();
  const tenantId = sessionDB.getTenantId() || '';
  const sessionAccessToken = sessionDB.getAccessToken() || accessToken || '';
  const tenantCodeFromSession = sessionDB.getTenantCode() || tenantCode || '';

  const fetchInfo = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const searchHeaders = {
        'x-tenant-code': tenantCodeFromSession || '',
        'x-tenant-id': String(tenantId || ''),
        'x-service-code': 'HR01S',
        'authorization': `Bearer ${sessionAccessToken}`
      };
      const response = await fetch(
        resolveApiUrl(`/saas/hrms/emp-info/search/${initialEmployeeId}`),
        { method: 'GET', headers: searchHeaders }
      );
      if (!response.ok) return;

      const body = await response.json();
      const candidates = [
      body?.data?.content?.[0],
      body?.data?.records?.[0],
      body?.data?.data?.[0],
      body?.data?.[0],
      body?.content?.[0],
      body?.data?.data,
      body?.data,
      body];

      // Prefer a real employee master record over pagination envelopes
      const data =
      candidates.find(
        (c) =>
        c &&
        typeof c === "object" &&
        !Array.isArray(c) && (
        c.firstName || c.lastName || c.email || c.empId || c.fullName)
      ) ||
      candidates.find((c) => c && typeof c === "object" && !Array.isArray(c)) ||
      {};

      if (!data || Object.keys(data).length === 0) return;

      setForm({
        firstName: data.firstName || '',
        middleName: data.middleName || '',
        lastName: data.lastName || '',
        dateOfBirth: data.dateOfBirth ? String(data.dateOfBirth).split("T")[0] : '',
        email: data.email || '',
        phone: data.phone || '',
        contactType: data.contactType || 'Phone',
        addressHouseNo: data.addressHouseNo || '',
        addressStreetName: data.addressStreetName || '',
        addressLocationArea: data.addressLocationArea || '',
        addressDistrict: data.addressDistrict || '',
        addressState: data.addressState || '',
        addressPincode: data.addressPincode || '',
        addressDigipin: data.addressDigipin || '',
        addressGmapLink: data.addressGmapLink || ''
      });

      const existingPhoto = findPhotoInRecord(data);
      const hasPhotoFlag = recordHasPhotoFlag(data);
      const explicitNoPhoto =
      data.hasEmpPic === false ||
      data.hasEmpPic === "false" ||
      data.hasEmpPic === 0 ||
      data.hasEmpPic === "N";

      if (existingPhoto) {
        clearPhotoObjectUrl();
        applyPhotoToState(existingPhoto, setPhotoSrc, setPhotoB64);
      } else if (hasPhotoFlag) {
        try {
          const downloaded = await fetchEmployeePhotoFromBackend(initialEmployeeId, {
            "x-tenant-code": tenantCodeFromSession || "",
            "x-tenant-id": String(tenantId || ""),
            authorization: `Bearer ${sessionAccessToken}`
          });
          if (downloaded?.kind === "inline") {
            clearPhotoObjectUrl();
            applyPhotoToState(downloaded.value, setPhotoSrc, setPhotoB64);
          } else if (downloaded?.kind === "blob") {
            clearPhotoObjectUrl();
            const objectUrl = URL.createObjectURL(downloaded.value);
            photoObjectUrlRef.current = objectUrl;
            setPhotoSrc(objectUrl);
            // Keep photoB64 null so unchanged photos are not re-posted on update
            setPhotoB64(null);
          }
          // If download fails, keep any in-memory preview (e.g. just uploaded in Add flow)
        } catch (photoErr) {
          console.error("Failed to download employee photo", photoErr);
        }
      } else if (explicitNoPhoto) {
        clearPhotoObjectUrl();
        setPhotoSrc(null);
        setPhotoB64(null);
      }
      // else: no photo metadata — do not wipe a local preview still in state

      setSelectedStateId('');
    } catch (err) {
      console.error("Failed to load employee master info", err);
    }
  }, [initialEmployeeId, sessionAccessToken, tenantCodeFromSession, tenantId, clearPhotoObjectUrl]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm(INITIAL);
      setSelectedStateId('');
      return;
    }
    fetchInfo();
  }, [isActive, initialEmployeeId, fetchInfo]);

  const fetchOrgLookup = useCallback(async (path) => {
    if (!tenantId) {
      throw new Error(i18n.t('onboarding.errors.tenantIdMissing'));
    }

    const requestPath = `${path}${path.includes('?') ? '&' : '?'}tenantId=${encodeURIComponent(String(tenantId))}`;
    const nextTenantId = String(tenantId || '').trim();
    const nextTenantCode = String(tenantCodeFromSession || '').trim();
    const nextAccessToken = String(sessionAccessToken || '').trim();
    const response = await fetch(resolveOrgApiUrl(requestPath), {
      method: 'GET',
      headers: {
        Accept: '*/*',
        'x-service-code': 'HR20A',
        ...(nextAccessToken ? { Authorization: `Bearer ${nextAccessToken}` } : {}),
        ...(nextTenantId ? { 'X-Tenant-Id': nextTenantId } : {}),
        ...(nextTenantCode ? { 'X-Tenant-Code': nextTenantCode } : {})
      }
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message = `Lookup request failed (${response.status})`;
      const error = new Error(message);
      error.status = response.status;
      error.errorBody = errorBody;
      throw error;
    }

    return response.json().catch(() => ({}));
  }, [sessionAccessToken, tenantCodeFromSession, tenantId]);

  /* ── Field change ─────────────────────────────────────── */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    const rules = getRules();
    if (touched[name] && rules[name]) {
      setErrors((er) => ({ ...er, [name]: rules[name](value) }));
    }
  }, [touched]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
    const rules = getRules();
    if (rules[name]) {
      setErrors((er) => ({ ...er, [name]: rules[name](value) }));
    }
  }, []);

  const handleStateChange = useCallback((nextStateId) => {
    const selectedOption = stateOptions.find((option) => option.value === nextStateId);
    const rules = getRules();
    setSelectedStateId(nextStateId);
    setSelectedDistrictId('');
    setDistrictOptions([]);
    setForm((prev) => ({
      ...prev,
      addressState: selectedOption?.label || '',
      addressDistrict: ''
    }));
    setTouched((prev) => ({ ...prev, addressState: true, addressDistrict: false }));
    setErrors((prev) => ({
      ...prev,
      addressState: rules.addressState(selectedOption?.label || ''),
      addressDistrict: ''
    }));
  }, [stateOptions]);

  const handleDistrictChange = useCallback((nextDistrictId) => {
    const selectedOption = districtOptions.find((option) => option.value === nextDistrictId);
    const rules = getRules();
    setSelectedDistrictId(nextDistrictId);
    setForm((prev) => ({
      ...prev,
      addressDistrict: selectedOption?.label || ''
    }));
    setTouched((prev) => ({ ...prev, addressDistrict: true }));
    setErrors((prev) => ({
      ...prev,
      addressDistrict: rules.addressDistrict(selectedOption?.label || '')
    }));
  }, [districtOptions]);

  /* ── Photo ────────────────────────────────────────────── */
  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      setBanner({ type: 'error', msg: i18n.t('onboarding.errors.photoExceeds5Mb') });
      return;
    }
    clearPhotoObjectUrl();
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setPhotoSrc(dataUrl);
      setPhotoB64(dataUrl.split(',')[1]);
    };
    reader.readAsDataURL(file);
  }

  function removePhoto() {
    clearPhotoObjectUrl();
    setPhotoSrc(null);
    setPhotoB64(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  useEffect(() => () => clearPhotoObjectUrl(), [clearPhotoObjectUrl]);

  useEffect(() => {
    if (!isActive) return;
    let active = true;

    async function loadStates() {
      if (!tenantId) {
        if (active) {
          setStateOptions([]);
          setDistrictOptions([]);
        }
        return;
      }

      setStatesLoading(true);
      try {
        const data = await fetchOrgLookup(STATES_ENDPOINT);
        if (!active) return;
        setStateOptions(normalizeLookupOptions(data, ['stateId', 'id'], ['stateName', 'name']));
      } catch (error) {
        if (!active) return;
        setStateOptions([]);
        if (reportLookupRateLimit(error.message, error.status, error.errorBody)) return;
        setBanner((current) => current || { type: 'error', msg: error.message || i18n.t('onboarding.errors.unableToLoadStates') });
      } finally {
        if (active) setStatesLoading(false);
      }
    }

    loadStates();
    return () => {
      active = false;
    };
  }, [isActive, fetchOrgLookup, reportLookupRateLimit, tenantId]);

  useEffect(() => {
    if (!form.addressState.trim() || selectedStateId || stateOptions.length === 0) return;
    const match = stateOptions.find((option) => option.label.toLowerCase() === form.addressState.trim().toLowerCase());
    if (match) {
      setSelectedStateId(match.value);
    }
  }, [form.addressState, selectedStateId, stateOptions]);

  useEffect(() => {
    if (!isActive) return;
    let active = true;

    async function loadDistricts() {
      if (!tenantId || !selectedStateId) {
        if (active) {
          setDistrictOptions([]);
          setDistrictsLoading(false);
        }
        return;
      }

      setDistrictsLoading(true);
      try {
        const data = await fetchOrgLookup(`${DISTRICTS_BY_STATE_ENDPOINT}/${encodeURIComponent(selectedStateId)}`);
        if (!active) return;
        const options = normalizeLookupOptions(data, ['cityId', 'districtId', 'id'], ['cityName', 'districtName', 'name']);
        setDistrictOptions(options);
        if (form.addressDistrict.trim()) {
          const selected = options.find((option) => option.label.toLowerCase() === form.addressDistrict.trim().toLowerCase());
          setSelectedDistrictId(selected?.value || '');
        }
      } catch (error) {
        if (!active) return;
        setDistrictOptions([]);
        if (reportLookupRateLimit(error.message, error.status, error.errorBody)) return;
        setBanner((current) => current || { type: 'error', msg: error.message || i18n.t('onboarding.errors.unableToLoadDistricts') });
      } finally {
        if (active) setDistrictsLoading(false);
      }
    }

    loadDistricts();
    return () => {
      active = false;
    };
  }, [isActive, fetchOrgLookup, form.addressDistrict, reportLookupRateLimit, selectedStateId, tenantId]);

  /* ── Validate all ─────────────────────────────────────── */
  function validateAll() {
    const rules = getRules();
    const newErrors = {};
    let valid = true;
    Object.keys(rules).forEach((field) => {
      const msg = rules[field](form[field] || '');
      if (msg) {newErrors[field] = msg;valid = false;}
    });
    setErrors(newErrors);
    // Mark all required fields as touched so errors show
    const allTouched = {};
    REQUIRED_FIELDS.forEach((f) => {allTouched[f] = true;});
    setTouched(allTouched);
    return valid;
  }

  /* ── Submit ───────────────────────────────────────────── */
  async function handleSubmit() {
    setBanner(null);
    if (!validateAll()) {
      return;
    }

    setLoading(true);

    const payload = {
      firstName: form.firstName.trim(),
      middleName: isEdit ? form.middleName.trim() : form.middleName.trim() || undefined,
      lastName: form.lastName.trim(),
      dateOfBirth: form.dateOfBirth,
      email: isEdit ? form.email.trim() : form.email.trim() || undefined,
      phone: form.phone.trim(),
      contactType: form.contactType,
      addressHouseNo: form.addressHouseNo.trim(),
      addressStreetName: form.addressStreetName.trim(),
      addressLocationArea: form.addressLocationArea.trim(),
      addressDistrict: form.addressDistrict.trim(),
      addressState: form.addressState.trim(),
      addressPincode: form.addressPincode.trim(),
      addressDigipin: isEdit ? form.addressDigipin.trim() : form.addressDigipin.trim() || undefined,
      addressGmapLink: form.addressGmapLink.trim(),
      empPicBase64: photoB64 || undefined,
      photoBase64: photoB64 || undefined,
      empPic: photoB64 || undefined
    };

    // Remove undefined keys (cleaner JSON body)
    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);

    const endpoint = isEdit ? `/saas/hrms/emp-info/update/${initialEmployeeId}` : API_ENDPOINT;
    const method = isEdit ? 'PUT' : 'POST';
    const serviceCode = isEdit ? 'HR01U' : SERVICE_CODE;

    try {
      const response = await fetch(resolveApiUrl(endpoint), {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-code': tenantCodeFromSession || '',
          'x-tenant-id': String(tenantId || ''),
          'x-service-code': serviceCode,
          'authorization': `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const responseRecord = data?.data && typeof data.data === "object" ? data.data : data;
        const responsePhoto = findPhotoInRecord(responseRecord);
        if (responsePhoto) {
          clearPhotoObjectUrl();
          applyPhotoToState(responsePhoto, setPhotoSrc, setPhotoB64);
        } else if (photoB64 && !photoSrc) {
          applyPhotoToState(photoB64, setPhotoSrc, setPhotoB64);
        }

        setBanner({ type: 'success', msg: isEdit ? i18n.t('onboarding.errors.employeeUpdatedSuccessfully') : i18n.t('onboarding.errors.employeeAddedSuccessfully') });
        if (isEdit) {
          await fetchInfo();
        }
        setTimeout(() => {if (onSuccess) onSuccess(data);}, 1200);
      } else {
        const fieldLabels = getFieldLabels();
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, fieldLabels);
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels });
      }
    } catch (err) {
      setBanner({ type: 'error', msg: i18n.t('onboarding.errors.couldNotReachServer') });
    } finally {
      setLoading(false);
    }
  }

  /* ── Shared input props ──────────────────────────────── */
  function inputProps(name, extra = {}) {
    return {
      id: name,
      name: name,
      value: form[name],
      onChange: handleChange,
      onBlur: handleBlur,
      className: `aep-input${errors[name] && touched[name] ? ' error' : ''}`,
      ...extra
    };
  }

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div className={`aep-page${isTabMode ? ' is-tab-mode' : ''}`}>
      {/* Nav */}
      {!isTabMode &&
      <header className="aep-nav">
          <div className="aep-nav-inner">
            <button className="aep-back-btn" onClick={onBack} aria-label={i18n.t("formActions.goBack")}>
              <IconBack />
            </button>
            <span className="aep-nav-title"><Trans i18nKey="formActions.addEmployee" /></span>
          </div>
        </header>
      }

      <main className="aep-main">
        {/* Breadcrumb */}
        {!isTabMode && breadcrumb &&
        <div className="aep-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, i) =>
          <React.Fragment key={item.label}>
                {i > 0 && <span className="aep-bc-sep" aria-hidden="true">/</span>}
                <button
              className={`aep-bc-item${item.active ? ' active' : ''}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>
              
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="aep-page-header">
            <h1 className="aep-page-title"><Trans i18nKey="formActions.addEmployee" /></h1>
            <p className="aep-page-sub"><Trans i18nKey="onboarding.fillInTheDetailsToRegisterANew" /></p>
          </div>
        }

        <div className="aep-form-card">

          {/* ── Photo ──────────────────────────────────── */}
          <div className="aep-section">
            <p className="aep-section-title">
              <Trans i18nKey="onboarding.employeePhoto" />
            </p>
            <div className="aep-photo-wrap">
              {photoSrc ?
              <>
                    <img src={photoSrc} alt={i18n.t("onboarding.preview")} className="aep-photo-preview visible" />
                    <button className="aep-photo-remove visible" type="button" onClick={removePhoto}>
                      <Trans i18nKey="formActions.removePhoto" />
                    </button>
                  </> :
              <div
                className="aep-photo-drop"
                onClick={() => fileRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
                aria-label={i18n.t("formActions.uploadEmployeePhoto")}>
                
                    <p className="aep-photo-text"><Trans i18nKey="onboarding.tapToUploadPhoto" /></p>
                    <p className="aep-photo-hint"><Trans i18nKey="onboarding.jpgOrPngMax5Mb" /></p>
                  </div>
              }
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                style={{ display: 'none' }}
                onChange={handlePhotoChange}
                aria-label={i18n.t("onboarding.photoFileInput")} />
              
            </div>
          </div>

          {/* ── Personal details ───────────────────────── */}
          <div className="aep-section">
            <p className="aep-section-title">
              <Trans i18nKey="onboarding.personalDetails" />
            </p>
            <div className="aep-grid">
              <Field id="firstName" label={i18n.t('onboarding.labels.firstName')} required error={touched.firstName && errors.firstName}>
                <input {...inputProps('firstName', { maxLength: 100, placeholder: i18n.t('onboarding.mandatory'), autoComplete: 'given-name' })} />
              </Field>

              <Field id="middleName" label={i18n.t('onboarding.labels.middleName')} error={touched.middleName && errors.middleName}>
                <input {...inputProps('middleName', { maxLength: 100, placeholder: i18n.t('common.optional') })} />
              </Field>

              <Field id="lastName" label={i18n.t('onboarding.labels.lastName')} required error={touched.lastName && errors.lastName}>
                <input {...inputProps('lastName', { maxLength: 100, placeholder: i18n.t('onboarding.mandatory'), autoComplete: 'family-name' })} />
              </Field>

              <Field id="dateOfBirth" label={i18n.t('onboarding.labels.dateOfBirth')} required error={touched.dateOfBirth && errors.dateOfBirth}>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={`aep-input${errors.dateOfBirth && touched.dateOfBirth ? ' error' : ''} w-full h-auto justify-start text-left font-normal`}>
                      
                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                      {form.dateOfBirth ?
                      format(new Date(form.dateOfBirth), 'PPP') :

                      <span className="text-muted-foreground"><Trans i18nKey="dateTime.pickADate" /></span>
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                    <Calendar
                      mode="single"
                      selected={form.dateOfBirth ? new Date(form.dateOfBirth) : undefined}
                      disabled={{ after: new Date() }}
                      captionLayout="dropdown"
                      startMonth={new Date(1950, 0)}
                      endMonth={new Date()}
                      onSelect={(date) => {
                        if (!date) return;
                        const v = format(date, 'yyyy-MM-dd');
                        setForm((prev) => ({ ...prev, dateOfBirth: v }));
                        setTouched((prev) => ({ ...prev, dateOfBirth: true }));
                        setErrors((prev) => ({ ...prev, dateOfBirth: getRules().dateOfBirth(v) }));
                      }}
                      initialFocus
                      className="p-3 pointer-events-auto" />
                    
                  </PopoverContent>
                </Popover>
              </Field>
            </div>
          </div>

          {/* ── Contact ────────────────────────────────── */}
          <div className="aep-section">
            <p className="aep-section-title">
              <Trans i18nKey="common.contact3" />
            </p>
            <div className="aep-grid">
              <Field id="email" label={i18n.t('common.email')} required error={touched.email && errors.email}>
                <input {...inputProps('email', { type: 'email', maxLength: 150, placeholder: i18n.t('onboarding.mandatory'), autoComplete: 'email' })} />
              </Field>

              <Field id="phone" label={i18n.t('onboarding.labels.phoneNumber')} required error={touched.phone && errors.phone}>
                <input {...inputProps('phone', { type: 'tel', maxLength: 12, placeholder: i18n.t('onboarding.max12Digits'), autoComplete: 'tel' })} />
              </Field>

              <Field id="contactType" label={i18n.t('onboarding.labels.preferredContact')} required error={touched.contactType && errors.contactType}>
                <div className="aep-select-wrap">
                  <select
                    id="contactType"
                    name="contactType"
                    value={form.contactType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`aep-select${errors.contactType && touched.contactType ? ' error' : ''}`}>
                    
                    <option value=""><Trans i18nKey="onboarding.select" /></option>
                    <option value="Phone"><Trans i18nKey="common.phone" /></option>
                    <option value="Email"><Trans i18nKey="common.email" /></option>
                    <option value="Both"><Trans i18nKey="onboarding.both" /></option>
                  </select>
                </div>
              </Field>
            </div>
          </div>

          {/* ── Address ────────────────────────────────── */}
          <div className="aep-section">
            <p className="aep-section-title">
              <Trans i18nKey="formActions.address" />
            </p>
           <div className="aep-grid cols-3">
             <Field id="addressHouseNo" label={i18n.t('onboarding.labels.houseNo')} required error={touched.addressHouseNo && errors.addressHouseNo}>
               <input {...inputProps('addressHouseNo', { maxLength: 50, placeholder: i18n.t('onboarding.houseNo') })} />
             </Field>

             <Field id="addressStreetName" label={i18n.t('onboarding.labels.streetName')} required error={touched.addressStreetName && errors.addressStreetName}>
               <input {...inputProps('addressStreetName', { maxLength: 50, placeholder: i18n.t('onboarding.streetRoad') })} />
             </Field>

             <Field id="addressLocationArea" label={i18n.t('onboarding.labels.areaLocation')} required error={touched.addressLocationArea && errors.addressLocationArea}>
               <input {...inputProps('addressLocationArea', { maxLength: 50, placeholder: i18n.t('onboarding.areaOrLocality') })} />
             </Field>
           </div>


              <div className="aep-grid">
                <Field id="addressState" label={i18n.t('onboarding.labels.state')} required error={touched.addressState && errors.addressState}>
                  <Select
                  value={selectedStateId}
                  onValueChange={handleStateChange}
                  disabled={statesLoading || stateOptions.length === 0}
                  onOpenChange={(open) => {
                    if (!open) {
                      setTouched((prev) => ({ ...prev, addressState: true }));
                      setErrors((prev) => ({ ...prev, addressState: getRules().addressState(form.addressState) }));
                    }
                  }}>
                  
                    <SelectTrigger
                    id="addressState"
                    className={`aep-select${errors.addressState && touched.addressState ? ' error' : ''}`}>
                    
                      <SelectValue placeholder={statesLoading ? i18n.t('onboarding.loadingStates') : stateOptions.length ? i18n.t('onboarding.selectState') : i18n.t('onboarding.noStatesAvailable')} />
                    </SelectTrigger>
                    <SelectContent>
                      {stateOptions.map((option) =>
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    )}
                    </SelectContent>
                  </Select>
                </Field>


                 <Field id="addressDistrict" label={i18n.t('onboarding.labels.district')} required error={touched.addressDistrict && errors.addressDistrict}>
                   <Select
                  value={selectedDistrictId}
                  onValueChange={handleDistrictChange}
                  disabled={!selectedStateId || districtsLoading || districtOptions.length === 0}
                  onOpenChange={(open) => {
                    if (!open) {
                      setTouched((prev) => ({ ...prev, addressDistrict: true }));
                      setErrors((prev) => ({ ...prev, addressDistrict: getRules().addressDistrict(form.addressDistrict) }));
                    }
                  }}>
                  
                     <SelectTrigger
                    id="addressDistrict"
                    className={`aep-select${errors.addressDistrict && touched.addressDistrict ? ' error' : ''}`}>
                    
                       <SelectValue
                      placeholder={
                      !selectedStateId ?
                      i18n.t('onboarding.selectStateFirst') :
                      districtsLoading ?
                      i18n.t('onboarding.loadingDistricts') :
                      districtOptions.length ?
                      i18n.t('onboarding.selectDistrict') :
                      i18n.t('onboarding.noDistrictsAvailable')
                      } />
                    
                     </SelectTrigger>
                     <SelectContent>
                       {districtOptions.map((option) =>
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    )}
                     </SelectContent>
                   </Select>
                 </Field>
                              </div>


              <div className="aep-grid">
              <Field id="addressPincode" label={i18n.t('onboarding.labels.pincode')} required error={touched.addressPincode && errors.addressPincode}>
                <input {...inputProps('addressPincode', { maxLength: 6, placeholder: i18n.t('onboarding.sixDigitCode'), inputMode: 'numeric', pattern: '[0-9]*' })} />
              </Field>

              <Field id="addressDigipin" label={i18n.t('onboarding.labels.digipin')} error={touched.addressDigipin && errors.addressDigipin}>
                <input {...inputProps('addressDigipin', { maxLength: 10, placeholder: i18n.t('common.optional') })} />
              </Field>
              </div>


              <div className="aep-grid full">
                              <Field id="addressGmapLink" label={i18n.t('onboarding.labels.googleMapsLink')} required error={touched.addressGmapLink && errors.addressGmapLink}>
                                <input {...inputProps('addressGmapLink', { type: 'url', placeholder: i18n.t('onboarding.googleMapsUrl') })} />
                              </Field>
                            </div>
                          </div>

          {/* ── Submit ─────────────────────────────────── */}
          {banner &&
          <div className={`aep-banner ${banner.type}`} role="alert">
              {banner.msg}
            </div>
          }

          {!isTabMode &&
          <div className="aep-section">
              <button
              className="aep-submit-btn"
              type="button"
              onClick={handleSubmit}
              disabled={loading}>
              
                {loading ?
              <>{isEdit ? i18n.t('onboarding.updatingEmployee') : i18n.t('onboarding.addingEmployee')}</> :
              <>{isEdit ? i18n.t('onboarding.updateEmployee') : <Trans i18nKey="formActions.addEmployee" />}</>
              }
              </button>
            </div>
          }

        </div>
      </main>
    </div>);

}
